import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Fingerprint, ArrowRight, ArrowLeft, Loader2, CalendarCheck, User, Package, Sparkles, RefreshCw, UserPlus, Download, ScanFace } from "lucide-react";
import { maskCPF, unmask } from "@/lib/masks";
import { KioskFaceCapture } from "@/components/KioskFaceCapture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API });

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; } };
const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

const Digit = ({ v, onClick, wide = false, testid, children }) => (
  <button
    type="button"
    data-testid={testid || `kiosk-key-${v}`}
    onClick={() => onClick(v)}
    className={`${wide ? "col-span-2" : ""} h-20 rounded-2xl bg-slate-900 hover:bg-black active:scale-95 transition text-white text-3xl font-heading font-bold shadow-[0_8px_30px_-12px_rgba(225,29,72,0.4)] border border-slate-800`}
  >
    {children || v}
  </button>
);

export default function Kiosk() {
  const { unitId } = useParams();
  const [stage, setStage] = useState("cpf"); // cpf | dashboard | plans | pix | success | expired
  const [cpf, setCpf] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { student, current_plan, payments }
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [pix, setPix] = useState(null);
  const [pixStatus, setPixStatus] = useState("pending");
  const pollRef = useRef(null);
  const idleRef = useRef(null);

  // Auto reset after 60s idle on cpf stage
  useEffect(() => {
    if (stage === "success" || stage === "pix" || stage === "face") return; // don't auto-reset while paying/capturing
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => resetAll(), 60_000);
    return () => clearTimeout(idleRef.current);
  }, [stage, cpf]);

  const bumpIdle = () => {
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => resetAll(), 60_000);
  };

  const resetAll = () => {
    // If a pending pix was generated but never paid, delete it server-side
    if (pix?.session_id && pixStatus !== "paid") {
      http.delete(`/payments/pending/${pix.session_id}`).catch(()=>{});
    }
    setStage("cpf"); setCpf(""); setData(null); setPlans([]);
    setSelectedPlan(null); setPix(null); setPixStatus("pending");
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pressKey = (k) => {
    bumpIdle();
    if (k === "back") return setCpf((c) => c.slice(0, -1));
    if (k === "clear") return setCpf("");
    const digits = unmask(cpf) + k;
    if (digits.length <= 11) setCpf(maskCPF(digits));
  };

  const submitCpf = async () => {
    const digits = unmask(cpf);
    if (digits.length !== 11) return toast.error("Digite os 11 dígitos do CPF");
    setLoading(true);
    try {
      const { data: d } = await http.post("/kiosk/lookup", { cpf: digits });
      setData(d); setStage("dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "CPF não encontrado");
    } finally { setLoading(false); }
  };

  const openPlans = async () => {
    bumpIdle();
    try {
      const unit = unitId || data?.student?.unit_id;
      const { data: p } = await http.get("/kiosk/plans" + (unit ? `?unit_id=${unit}` : ""));
      setPlans(p || []);
      setStage("plans");
    } catch { toast.error("Erro ao carregar planos"); }
  };

  const startPix = async (plan) => {
    setSelectedPlan(plan);
    setLoading(true);
    try {
      const { data: r } = await http.post("/kiosk/pix/create", { student_id: data.student.id, plan_id: plan.id, unit_id: unitId || null });
      setPix(r); setPixStatus("pending"); setStage("pix");
      pollRef.current = setInterval(pollStatus(r.session_id), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar Pix");
    } finally { setLoading(false); }
  };

  const pollStatus = (sid) => async () => {
    try {
      const { data: st } = await http.get(`/kiosk/pix/status/${sid}`);
      setPixStatus(st.payment_status);
      if (st.payment_status === "paid") {
        clearInterval(pollRef.current); pollRef.current = null;
        // refresh dashboard data after payment
        const { data: fresh } = await http.post("/kiosk/lookup", { cpf: unmask(cpf) });
        setData(fresh);
        setStage("success");
      }
    } catch {}
  };

  const confirmMockPix = async () => {
    if (!pix?.session_id) return;
    setLoading(true);
    try {
      await http.post(`/kiosk/pix/confirm/${pix.session_id}`);
      // trigger next poll ASAP
      await new Promise((r) => setTimeout(r, 500));
      const { data: st } = await http.get(`/kiosk/pix/status/${pix.session_id}`);
      setPixStatus(st.payment_status);
      if (st.payment_status === "paid") {
        clearInterval(pollRef.current); pollRef.current = null;
        const { data: fresh } = await http.post("/kiosk/lookup", { cpf: unmask(cpf) });
        setData(fresh);
        setStage("success");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const [faceSaving, setFaceSaving] = useState(false);
  const saveFace = async (cap) => {
    setFaceSaving(true);
    try {
      await http.post("/kiosk/face", { cpf: unmask(cpf), unit_id: unitId || null, face_photo: cap.photo, face_embedding: cap.descriptor });
      const { data: fresh } = await http.post("/kiosk/lookup", { cpf: unmask(cpf) });
      setData(fresh); setStage("face_done");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar a facial"); }
    finally { setFaceSaving(false); }
  };

  // PWA: swap to totem manifest + capture install prompt for a button on-screen
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  useEffect(() => {
    // 1) swap manifest to the totem one (fullscreen + start_url=/totem)
    const link = document.querySelector('link[rel="manifest"]');
    const original = link?.getAttribute("href");
    if (link) link.setAttribute("href", "/totem-manifest.webmanifest");
    // 2) hint fullscreen + landscape for supported browsers
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const origTheme = themeMeta?.getAttribute("content");
    themeMeta?.setAttribute("content", "#E11D48");
    // 3) Prevent scroll bounce on tablets
    document.body.style.overscrollBehavior = "none";
    // 4) Capture beforeinstallprompt
    const onBIP = (e) => { e.preventDefault(); setInstallPromptEvt(e); };
    window.addEventListener("beforeinstallprompt", onBIP);
    return () => {
      if (link && original) link.setAttribute("href", original);
      if (themeMeta && origTheme) themeMeta.setAttribute("content", origTheme);
      document.body.style.overscrollBehavior = "";
      window.removeEventListener("beforeinstallprompt", onBIP);
    };
  }, []);

  const installPwa = async () => {
    if (!installPromptEvt) {
      toast.info("Menu do navegador → 'Adicionar à tela inicial' pra instalar como app.");
      return;
    }
    installPromptEvt.prompt();
    const { outcome } = await installPromptEvt.userChoice;
    if (outcome === "accepted") toast.success("Totem instalado!");
    setInstallPromptEvt(null);
  };

  // Pré-permissão de câmera no boot do totem — grava o "sim" do usuário
  // para que a Venda sem cadastro abra a câmera direto na etapa da foto.
  const [camPrimed, setCamPrimed] = useState(() => {
    try { return sessionStorage.getItem("totem_cam_primed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    let cancelled = false;
    const prime = async () => {
      if (camPrimed) return;
      if (!navigator?.mediaDevices?.getUserMedia || !window.isSecureContext) return;
      // Se já está "granted" no browser, só marca e sai
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: "camera" });
          if (status.state === "granted") {
            sessionStorage.setItem("totem_cam_primed", "1");
            if (!cancelled) setCamPrimed(true);
            return;
          }
          if (status.state === "denied") {
            return; // não podemos re-pedir sem gesto do usuário — fica pro botão do fluxo
          }
        }
      } catch {}
      // Estado "prompt" → aguarda um gesto do usuário antes de pedir (browsers exigem)
    };
    prime();
    return () => { cancelled = true; };
  }, [camPrimed]);

  // Ao primeiro toque em qualquer lugar da tela, se ainda não pedimos, pedimos agora
  const primeCamOnGesture = async () => {
    if (camPrimed) return;
    if (!navigator?.mediaDevices?.getUserMedia || !window.isSecureContext) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "user" } }, audio: false });
      // Parar imediatamente — só queríamos o consentimento
      stream.getTracks().forEach(t => t.stop());
      sessionStorage.setItem("totem_cam_primed", "1");
      setCamPrimed(true);
      toast.success("Câmera pronta pra próxima venda sem cadastro");
    } catch (err) {
      // Silencioso — se negou, mostra só um aviso curto
      if (err?.name === "NotAllowedError") {
        toast.info("Você pode permitir a câmera depois no cadeado do navegador.");
      }
    }
  };

  return (
    <div className="min-h-[100dvh] max-h-[100dvh] overflow-y-auto bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white flex flex-col" onTouchStart={(e)=>{ bumpIdle(); primeCamOnGesture(); }} onMouseMove={bumpIdle} onClick={primeCamOnGesture}>
      {/* Header */}
      <header className="px-4 md:px-8 py-4 md:py-6 flex items-center justify-between border-b border-white/5 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-rose-700 flex items-center justify-center shadow-[0_0_40px_-8px_rgba(225,29,72,0.6)]">
            <Fingerprint className="w-6 h-6 md:w-7 md:h-7 text-white" />
          </div>
          <div>
            <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-rose-400 font-bold">American Fit</div>
            <div className="font-heading text-lg md:text-2xl font-black uppercase tracking-tight">Totem de Autoatendimento</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {installPromptEvt && stage === "cpf" && (
            <Button onClick={installPwa} data-testid="kiosk-install-pwa" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-xs">
              <Download className="w-4 h-4 mr-1"/>Instalar
            </Button>
          )}
          {camPrimed && stage === "cpf" && (
            <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-[10px] text-emerald-300 uppercase font-bold tracking-widest" data-testid="kiosk-cam-primed-badge" title="Câmera com permissão para a próxima venda sem cadastro">
              <CheckCircle2 className="w-3 h-3"/>Câmera OK
            </div>
          )}
          {stage !== "cpf" && (
            <Button variant="ghost" onClick={resetAll} data-testid="kiosk-exit-button" className="text-white/70 hover:text-white hover:bg-white/10 uppercase font-bold">
              Sair
            </Button>
          )}
        </div>
      </header>

      <main className="flex-1 flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto">
        {stage === "cpf" && (
          <div className="w-full max-w-xl text-center" data-testid="kiosk-cpf-stage">
            <div className="mb-8">
              <h1 className="font-heading text-5xl font-black uppercase leading-tight text-rose-500">Bem-vindo!</h1>
              <p className="text-white/60 mt-3 text-lg">Digite seu CPF para consultar seu contrato e comprar um novo plano.</p>
            </div>
            <div className="mb-6 py-6 px-4 rounded-3xl bg-white/5 border border-white/10 backdrop-blur">
              <div className="text-xs uppercase tracking-[0.3em] text-rose-400 font-bold mb-2">CPF</div>
              <div className="font-heading text-5xl font-black tracking-widest tabular-nums h-16 flex items-center justify-center" data-testid="kiosk-cpf-display">
                {cpf || <span className="text-white/20">000.000.000-00</span>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {["1","2","3","4","5","6","7","8","9"].map((d) => (
                <Digit key={d} v={d} onClick={pressKey} />
              ))}
              <Digit v="clear" onClick={pressKey} testid="kiosk-key-clear"><span className="text-lg">Limpar</span></Digit>
              <Digit v="0" onClick={pressKey} />
              <Digit v="back" onClick={pressKey} testid="kiosk-key-back"><ArrowLeft className="w-6 h-6 mx-auto"/></Digit>
            </div>
            <Button
              disabled={loading || unmask(cpf).length !== 11}
              onClick={submitCpf}
              data-testid="kiosk-cpf-submit"
              className="w-full h-16 bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-700 hover:to-rose-600 disabled:opacity-40 text-white text-xl font-heading font-black uppercase tracking-wider rounded-2xl shadow-[0_10px_40px_-10px_rgba(225,29,72,0.6)]">
              {loading ? <Loader2 className="w-6 h-6 animate-spin"/> : (<><ArrowRight className="w-6 h-6 mr-2"/>Entrar</>)}
            </Button>

            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10"/>
              <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Ainda não é aluno?</span>
              <div className="flex-1 h-px bg-white/10"/>
            </div>
            <Button
              type="button"
              onClick={() => { window.location.href = unitId ? `/autocadastro/${unitId}` : "/autocadastro"; }}
              data-testid="kiosk-anon-signup"
              className="mt-3 w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-heading font-black uppercase tracking-wider rounded-2xl shadow-[0_10px_40px_-10px_rgba(16,185,129,0.55)]">
              <UserPlus className="w-5 h-5 mr-2"/>Venda sem cadastro
            </Button>
            <p className="mt-2 text-[11px] text-white/50 leading-relaxed">
              Toque para se cadastrar em 3 minutos: dados, foto pela câmera do tablet e pagamento via Pix. Sua facial ativa a catraca automaticamente ao fim.
            </p>
          </div>
        )}

        {stage === "dashboard" && data && (
          <div className="w-full max-w-4xl" data-testid="kiosk-dashboard-stage">
            <div className="flex items-center gap-5 mb-8">
              {data.student.photo ? (
                <img src={data.student.photo} alt="" className="w-24 h-24 rounded-full object-cover border-4 border-rose-500"/>
              ) : (
                <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center border-4 border-rose-500">
                  <User className="w-10 h-10 text-white/60"/>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-rose-400 font-bold">Olá,</div>
                <h1 className="font-heading text-4xl font-black uppercase leading-tight text-rose-500" data-testid="kiosk-student-name">{data.student.name}</h1>
                {data.student.unit_name && <div className="text-white/50 text-sm mt-1">{data.student.unit_name}</div>}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <Card className="p-6 bg-white/5 border-white/10 backdrop-blur">
                <div className="flex items-center gap-2 text-rose-400 uppercase text-xs font-bold tracking-widest mb-3"><CalendarCheck className="w-4 h-4"/>Status do Contrato</div>
                {data.student.active ? (
                  <>
                    <div className="flex items-center gap-2 text-emerald-400 font-heading font-black text-2xl uppercase" data-testid="kiosk-active-badge">
                      <CheckCircle2 className="w-7 h-7"/>Ativo
                    </div>
                    <div className="text-white/80 mt-3">
                      <div className="text-xs uppercase text-white/50 tracking-widest">Plano atual</div>
                      <div className="font-heading text-xl font-bold">{data.current_plan?.name || "—"}</div>
                    </div>
                    <div className="text-white/80 mt-3">
                      <div className="text-xs uppercase text-white/50 tracking-widest">Válido até</div>
                      <div className="font-heading text-xl font-bold" data-testid="kiosk-membership-end">{fmt(data.student.membership_end)}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-rose-400 font-heading font-black text-2xl uppercase" data-testid="kiosk-expired-badge">
                      <XCircle className="w-7 h-7"/>Vencido
                    </div>
                    <p className="text-white/70 mt-3">Seu plano venceu {data.student.membership_end ? `em ${fmt(data.student.membership_end)}` : "ou nunca foi ativado"}. Renove agora para voltar a treinar!</p>
                  </>
                )}
              </Card>

              <Card className="p-6 bg-white/5 border-white/10 backdrop-blur">
                <div className="flex items-center gap-2 text-rose-400 uppercase text-xs font-bold tracking-widest mb-3"><Package className="w-4 h-4"/>Últimos Pagamentos</div>
                {data.payments.length === 0 ? (
                  <div className="text-white/50 text-sm">Nenhum pagamento registrado.</div>
                ) : (
                  <div className="space-y-2">
                    {data.payments.slice(0, 4).map((p) => (
                      <div key={p.session_id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                        <div className="text-white/70">{fmt(p.created_at)}</div>
                        <div className="font-mono font-bold text-emerald-400">{money((p.amount || 0) / 100)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Button
                onClick={openPlans}
                data-testid="kiosk-buy-plan-button"
                className="h-24 bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-700 hover:to-rose-600 text-white text-xl font-heading font-black uppercase tracking-wider rounded-2xl shadow-[0_10px_40px_-10px_rgba(225,29,72,0.6)]">
                <Sparkles className="w-6 h-6 mr-2"/>Comprar plano
              </Button>
              <Button
                onClick={openPlans}
                data-testid="kiosk-renew-plan-button"
                className="h-24 bg-white/10 hover:bg-white/20 border-2 border-white/20 text-white text-xl font-heading font-black uppercase tracking-wider rounded-2xl">
                <RefreshCw className="w-6 h-6 mr-2"/>Renovar plano
              </Button>
              <Button
                onClick={() => { bumpIdle(); setStage("face"); }}
                data-testid="kiosk-face-button"
                className="md:col-span-2 h-20 bg-white/10 hover:bg-white/20 border-2 border-emerald-500/40 text-white text-lg font-heading font-black uppercase tracking-wider rounded-2xl">
                <ScanFace className="w-6 h-6 mr-2 text-emerald-400"/>{data.student.has_face ? "Atualizar minha facial" : "Cadastrar minha facial"}
              </Button>
            </div>
          </div>
        )}

        {stage === "face" && data && (
          <KioskFaceCapture saving={faceSaving} onSave={saveFace} onCancel={() => setStage("dashboard")}/>
        )}

        {stage === "face_done" && data && (
          <div className="w-full max-w-xl text-center" data-testid="kiosk-face-done-stage">
            <div className="w-28 h-28 rounded-full bg-emerald-500/10 border-4 border-emerald-500 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-14 h-14 text-emerald-400"/>
            </div>
            <h1 className="font-heading text-5xl font-black uppercase mb-2 text-rose-500">Facial cadastrada!</h1>
            <p className="text-white/70 mb-8">Em alguns minutos a catraca reconhece seu rosto.</p>
            <Button onClick={resetAll} data-testid="kiosk-face-done-close" className="w-full h-16 bg-gradient-to-r from-rose-600 to-rose-500 text-white text-xl font-heading font-black uppercase rounded-2xl">Concluir</Button>
          </div>
        )}

        {stage === "plans" && (
          <div className="w-full max-w-5xl" data-testid="kiosk-plans-stage">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-4xl font-black uppercase text-rose-500">Escolha seu plano</h1>
              <Button variant="ghost" onClick={() => setStage("dashboard")} data-testid="kiosk-back-plans" className="text-white/70 hover:text-white hover:bg-white/10 uppercase font-bold">
                <ArrowLeft className="w-5 h-5 mr-2"/>Voltar
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {plans.length === 0 && <div className="col-span-3 text-center text-white/50 py-16">Carregando planos...</div>}
              {plans.map((p) => (
                <button
                  key={p.id}
                  onClick={() => startPix(p)}
                  data-testid={`kiosk-plan-${p.id}`}
                  className="group text-left p-6 rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-white/10 hover:border-rose-500 hover:from-rose-950/40 hover:to-rose-900/20 transition active:scale-[0.98]">
                  <div className="text-xs uppercase tracking-[0.3em] text-rose-400 font-bold">Plano</div>
                  <div className="font-heading text-2xl font-black uppercase mt-1 mb-3 text-rose-500">{p.name}</div>
                  <div className="text-white/60 text-sm mb-4 min-h-[40px]">{p.description || `${p.duration_days ? `${p.duration_days} dias` : `${p.duration_months} mês(es)`} de acesso`}</div>
                  <div className="text-4xl font-heading font-black text-rose-500 group-hover:text-rose-400 transition">{money(p.price)}</div>
                  <div className="text-xs text-white/40 mt-2">{p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {stage === "pix" && pix && (
          <div className="w-full max-w-2xl text-center" data-testid="kiosk-pix-stage">
            <h1 className="font-heading text-4xl font-black uppercase mb-2 text-rose-500">Pague com Pix</h1>
            <p className="text-white/60 mb-6">Aponte a câmera do celular para o QR Code abaixo.</p>
            <Card className="p-6 bg-white rounded-3xl">
              {pix.qr_image_url ? (
                <img src={pix.qr_image_url} alt="QR Pix" className="mx-auto w-64 h-64" data-testid="kiosk-pix-qr"/>
              ) : (
                <div className="w-64 h-64 mx-auto bg-slate-100 flex items-center justify-center"><Loader2 className="animate-spin text-rose-600"/></div>
              )}
              <div className="text-xs uppercase tracking-[0.3em] text-rose-500 font-bold mt-4">Valor</div>
              <div className="font-heading text-4xl font-black text-rose-600">{money(pix.amount)}</div>
              <div className="text-sm text-rose-500 mt-1 font-semibold">{selectedPlan?.name}</div>
              {pix.pix_code && (
                <div className="mt-4 text-[10px] font-mono break-all text-rose-600 bg-slate-50 p-2 rounded" data-testid="kiosk-pix-code">
                  {pix.pix_code.slice(0, 120)}{pix.pix_code.length > 120 ? "..." : ""}
                </div>
              )}
            </Card>
            <div className="mt-6 flex items-center justify-center gap-3 text-rose-300">
              <Loader2 className="w-5 h-5 animate-spin"/>
              <span className="uppercase text-sm tracking-widest font-bold" data-testid="kiosk-pix-waiting">Aguardando pagamento...</span>
            </div>
            {pix.is_mock && (
              <div className="mt-6">
                <p className="text-xs text-amber-300 mb-2">Modo demonstração — Pix real requer ativação no Stripe.</p>
                <Button onClick={confirmMockPix} disabled={loading} data-testid="kiosk-pix-mock-confirm" className="bg-amber-500 hover:bg-amber-600 text-rose-700 font-bold uppercase">
                  {loading ? <Loader2 className="animate-spin"/> : "Simular pagamento (Demo)"}
                </Button>
              </div>
            )}
            <div className="mt-6">
              <Button variant="ghost" onClick={() => {
                if (pix?.session_id && pixStatus !== "paid") http.delete(`/payments/pending/${pix.session_id}`).catch(()=>{});
                if (pollRef.current) clearInterval(pollRef.current);
                setStage("plans"); setPix(null); setPixStatus("pending");
              }} className="text-white/60 hover:text-white hover:bg-white/10 uppercase" data-testid="kiosk-pix-cancel">
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {stage === "success" && data && (
          <div className="w-full max-w-xl text-center" data-testid="kiosk-success-stage">
            <div className="w-28 h-28 rounded-full bg-emerald-500/10 border-4 border-emerald-500 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-14 h-14 text-emerald-400"/>
            </div>
            <h1 className="font-heading text-5xl font-black uppercase mb-2 text-rose-500">Pagamento confirmado!</h1>
            <p className="text-white/70 mb-6">Seu plano <span className="text-rose-400 font-bold">{selectedPlan?.name}</span> está ativo.</p>
            <Card className="p-6 bg-white/5 border-white/10 mb-6">
              <div className="text-xs uppercase tracking-[0.3em] text-rose-400 font-bold">Válido até</div>
              <div className="font-heading text-4xl font-black text-rose-500" data-testid="kiosk-success-membership-end">{fmt(data.student.membership_end)}</div>
            </Card>
            <Button onClick={resetAll} data-testid="kiosk-success-close" className="w-full h-16 bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-700 hover:to-rose-600 text-white text-xl font-heading font-black uppercase rounded-2xl">
              Concluir
            </Button>
          </div>
        )}
      </main>

      <footer className="px-8 py-4 text-center text-white/30 text-xs uppercase tracking-widest">
        {new Date().toLocaleDateString("pt-BR")} · American Fit · Autoatendimento
      </footer>
    </div>
  );
}
